import React, { useState, useEffect } from 'react';
import { Settings as SettingsIcon, Wrench, AlertTriangle, CheckCircle, RefreshCw, Terminal } from 'lucide-react';
import Header from './Header';
import PermissionGuard from './PermissionGuard';
import { useAuth } from '../contexts/AuthContext';
import { getMaintenanceStatus, setMaintenanceMode, subscribeToMaintenanceStatus } from '../lib/maintenanceService';

const Settings = () => {
  const { currentUser, showCacheRefreshButtons, setShowCacheRefreshButtons, showConsoleLogs, setShowConsoleLogs } = useAuth();
  const [maintenanceStatus, setMaintenanceStatus] = useState({
    isMaintenanceMode: false,
    lastUpdatedAt: null,
    updatedBy: null,
    message: 'El sitio se encuentra en mantenimiento. Por favor, intenta más tarde.'
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    // Subscribe to maintenance status changes for real-time updates
    const unsubscribe = subscribeToMaintenanceStatus((status) => {
      setMaintenanceStatus(status);
      setIsLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const handleMaintenanceToggle = async () => {
    if (isSaving) return;
    
    try {
      setIsSaving(true);
      setError('');
      
      const newMode = !maintenanceStatus.isMaintenanceMode;
      await setMaintenanceMode(newMode, currentUser?.email || 'admin');
      
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);
      
    } catch (error) {
      console.error('Error updating maintenance mode:', error);
      setError(error.message || 'Error al actualizar el modo de mantenimiento');
    } finally {
      setIsSaving(false);
    }
  };

  const handleCacheRefreshToggle = async () => {
    try {
      await setShowCacheRefreshButtons(!showCacheRefreshButtons);
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);
    } catch (error) {
      console.error('Error updating cache refresh buttons setting:', error);
      setError('Error al actualizar la configuración de botones de caché');
    }
  };

  const handleConsoleLogsToggle = async () => {
    try {
      await setShowConsoleLogs(!showConsoleLogs);
      setShowSuccessMessage(true);
      setTimeout(() => setShowSuccessMessage(false), 3000);
    } catch (error) {
      console.error('Error updating console logs setting:', error);
      setError('Error al actualizar la configuración de logs de consola');
    }
  };

  const formatDate = (date) => {
    if (!date) return 'N/A';
    
    const dateObj = date instanceof Date ? date : date.toDate();
    const day = dateObj.getDate().toString().padStart(2, '0');
    const month = (dateObj.getMonth() + 1).toString().padStart(2, '0');
    const year = dateObj.getFullYear();
    const hours = dateObj.getHours().toString().padStart(2, '0');
    const minutes = dateObj.getMinutes().toString().padStart(2, '0');
    
    return `${day}/${month}/${year} ${hours}:${minutes}`;
  };

  return (
    <PermissionGuard allowedRoles={['admin']}>
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
        <Header />
        
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Page Header */}
          <div className="mb-8">
            <div className="flex items-center space-x-3">
              <SettingsIcon className="h-8 w-8 text-purple-600 dark:text-purple-400" />
              <div>
                <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Ajustes del Sistema</h1>
              </div>
            </div>
          </div>

          {/* Success Message */}
          {showSuccessMessage && (
            <div className="mb-6 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
              <div className="flex items-center">
                <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 mr-3" />
                <span className="text-green-800 dark:text-green-200 font-medium">
                  Configuración actualizada correctamente
                </span>
              </div>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
              <div className="flex items-center">
                <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400 mr-3" />
                <span className="text-red-800 dark:text-red-200 font-medium">{error}</span>
              </div>
            </div>
          )}

          {/* Maintenance Mode Section */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center space-x-3">
                <Wrench className="h-6 w-6 text-orange-600 dark:text-orange-400" />
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                    Modo de Mantenimiento
                  </h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Controla el acceso al sistema durante el mantenimiento
                  </p>
                </div>
              </div>
            </div>
            
            <div className="p-6">
              {isLoading ? (
                <div className="text-center py-4">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">Cargando configuración...</p>
                </div>
              ) : (
                <div className="space-y-6">
                  {/* Toggle Switch */}
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                        Activar modo de mantenimiento
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        {maintenanceStatus.isMaintenanceMode 
                          ? 'El sitio está actualmente en mantenimiento. Solo los administradores pueden acceder.'
                          : 'El sitio está operativo. Todos los usuarios pueden acceder normalmente.'
                        }
                      </p>
                    </div>
                    
                    <button
                      onClick={handleMaintenanceToggle}
                      disabled={isSaving}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 dark:focus:ring-purple-400 focus:ring-offset-2 dark:focus:ring-offset-gray-800 ${
                        maintenanceStatus.isMaintenanceMode
                          ? 'bg-red-600 hover:bg-red-700'
                          : 'bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600'
                      } ${isSaving ? 'opacity-50 cursor-not-allowed' : ''}`}
                    >
                      <span
                        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                          maintenanceStatus.isMaintenanceMode ? 'translate-x-6' : 'translate-x-1'
                        }`}
                      />
                    </button>
                  </div>

                  {/* Status Information */}
                  <div className={`rounded-lg p-4 ${
                    maintenanceStatus.isMaintenanceMode 
                      ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800'
                      : 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800'
                  }`}>
                    <div className="flex items-start space-x-3">
                      {maintenanceStatus.isMaintenanceMode ? (
                        <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400 mt-0.5" />
                      ) : (
                        <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400 mt-0.5" />
                      )}
                      <div className="flex-1">
                        <h4 className={`font-medium ${
                          maintenanceStatus.isMaintenanceMode 
                            ? 'text-red-800 dark:text-red-200'
                            : 'text-green-800 dark:text-green-200'
                        }`}>
                          {maintenanceStatus.isMaintenanceMode ? 'Sistema en Mantenimiento' : 'Sistema Operativo'}
                        </h4>
                        
                        {maintenanceStatus.isMaintenanceMode && (
                          <p className="text-sm text-red-700 dark:text-red-300 mt-1">
                            <strong>Mensaje mostrado a los usuarios:</strong><br />
                            "{maintenanceStatus.message}"
                          </p>
                        )}
                        
                        <div className={`text-xs mt-2 space-y-1 ${
                          maintenanceStatus.isMaintenanceMode 
                            ? 'text-red-600 dark:text-red-400'
                            : 'text-green-600 dark:text-green-400'
                        }`}>
                          {maintenanceStatus.lastUpdatedAt && (
                            <div>
                              <strong>Última actualización:</strong> {formatDate(maintenanceStatus.lastUpdatedAt)}
                            </div>
                          )}
                          {maintenanceStatus.updatedBy && (
                            <div>
                              <strong>Actualizado por:</strong> {maintenanceStatus.updatedBy}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Information Box */}
                  <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                    <div className="flex items-start space-x-3">
                      <AlertTriangle className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5" />
                      <div className="text-sm text-blue-800 dark:text-blue-200">
                        <h4 className="font-medium mb-2">Información importante:</h4>
                        <ul className="space-y-1 list-disc list-inside">
                          <li>Los usuarios con rol "solo_lectura" y "shopkeeper" no podrán acceder al sistema durante el mantenimiento.</li>
                          <li>Los administradores pueden acceder normalmente para realizar tareas de mantenimiento.</li>
                          <li>Los cambios se aplican inmediatamente a todos los usuarios conectados.</li>
                        </ul>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Cache Refresh Buttons Section */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 mt-6">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center space-x-3">
                <RefreshCw className="h-6 w-6 text-blue-600 dark:text-blue-400" />
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                    Botones de Actualización de Caché
                  </h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Controla la visibilidad de los botones de actualización de caché para todos los usuarios
                  </p>
                </div>
              </div>
            </div>
            
            <div className="p-6">
              <div className="space-y-6">
                {/* Toggle Switch */}
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                      Mostrar botones de actualización de caché
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      {showCacheRefreshButtons 
                        ? 'Los botones de actualización de caché son visibles en todas las listas para todos los usuarios.'
                        : 'Los botones de actualización de caché están ocultos en todas las listas para todos los usuarios.'
                      }
                    </p>
                  </div>
                  
                  <button
                    onClick={handleCacheRefreshToggle}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:ring-offset-2 dark:focus:ring-offset-gray-800 ${
                      showCacheRefreshButtons
                        ? 'bg-blue-600 hover:bg-blue-700'
                        : 'bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        showCacheRefreshButtons ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                {/* Status Information */}
                <div className={`rounded-lg p-4 ${
                  showCacheRefreshButtons 
                    ? 'bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800'
                    : 'bg-gray-50 dark:bg-gray-900/20 border border-gray-200 dark:border-gray-800'
                }`}>
                  <div className="flex items-start space-x-3">
                    <RefreshCw className={`h-5 w-5 mt-0.5 ${
                      showCacheRefreshButtons 
                        ? 'text-blue-600 dark:text-blue-400'
                        : 'text-gray-600 dark:text-gray-400'
                    }`} />
                    <div className="flex-1">
                      <h4 className={`font-medium ${
                        showCacheRefreshButtons 
                          ? 'text-blue-800 dark:text-blue-200'
                          : 'text-gray-800 dark:text-gray-200'
                      }`}>
                        {showCacheRefreshButtons ? 'Botones Visibles' : 'Botones Ocultos'}
                      </h4>
                      <p className={`text-sm mt-1 ${
                        showCacheRefreshButtons 
                          ? 'text-blue-700 dark:text-blue-300'
                          : 'text-gray-700 dark:text-gray-300'
                      }`}>
                        {showCacheRefreshButtons 
                          ? 'Los usuarios pueden actualizar manualmente los datos desde el servidor en:'
                          : 'Los botones de actualización de caché están ocultos en:'
                        }
                      </p>
                      <ul className={`text-xs mt-2 space-y-1 list-disc list-inside ${
                        showCacheRefreshButtons 
                          ? 'text-blue-600 dark:text-blue-400'
                          : 'text-gray-600 dark:text-gray-400'
                      }`}>
                        <li>Lista de Órdenes Activas</li>
                        <li>Lista de Todas las Órdenes</li>
                        <li>Lista de Clientes</li>
                        <li>Catálogo de Equipos</li>
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Information Box */}
                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <AlertTriangle className="h-5 w-5 text-yellow-600 dark:text-yellow-400 mt-0.5" />
                    <div className="text-sm text-yellow-800 dark:text-yellow-200">
                      <h4 className="font-medium mb-2">Información importante:</h4>
                      <ul className="space-y-1 list-disc list-inside">
                        <li>Esta configuración afecta a todos los usuarios independientemente de su rol.</li>
                        <li>Los usuarios de solo lectura también verán u ocultarán estos botones según esta configuración.</li>
                        <li>Los cambios se aplican inmediatamente en la interfaz de usuario.</li>
                        <li>Los datos se siguen actualizando automáticamente según la política de caché (30 minutos).</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Console Logs Section */}
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 mt-6">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex items-center space-x-3">
                <Terminal className="h-6 w-6 text-gray-600 dark:text-gray-400" />
                <div>
                  <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                    Logs de Consola
                  </h2>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    Controla la visibilidad de los mensajes informativos en la consola del desarrollador
                  </p>
                </div>
              </div>
            </div>
            
            <div className="p-6">
              <div className="space-y-6">
                {/* Toggle Switch */}
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                      Mostrar logs informativos en consola
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                      {showConsoleLogs 
                        ? 'Los mensajes informativos de caché, IndexedDB y otros sistemas se muestran en la consola.'
                        : 'Los mensajes informativos están ocultos. Solo se mostrarán errores críticos.'
                      }
                    </p>
                  </div>
                  
                  <button
                    onClick={handleConsoleLogsToggle}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 dark:focus:ring-blue-400 focus:ring-offset-2 dark:focus:ring-offset-gray-800 ${
                      showConsoleLogs
                        ? 'bg-blue-600 hover:bg-blue-700'
                        : 'bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600'
                    }`}
                  >
                    <span
                      className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        showConsoleLogs ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>

                {/* Status Information */}
                <div className={`rounded-lg p-4 ${
                  showConsoleLogs 
                    ? 'bg-gray-50 dark:bg-gray-900/20 border border-gray-200 dark:border-gray-800'
                    : 'bg-gray-50 dark:bg-gray-900/20 border border-gray-200 dark:border-gray-800'
                }`}>
                  <div className="flex items-start space-x-3">
                    <Terminal className={`h-5 w-5 mt-0.5 ${
                      showConsoleLogs 
                        ? 'text-gray-600 dark:text-gray-400'
                        : 'text-gray-400 dark:text-gray-600'
                    }`} />
                    <div className="flex-1">
                      <h4 className={`font-medium ${
                        showConsoleLogs 
                          ? 'text-gray-800 dark:text-gray-200'
                          : 'text-gray-800 dark:text-gray-200'
                      }`}>
                        {showConsoleLogs ? 'Logs Visibles' : 'Logs Ocultos'}
                      </h4>
                      <p className={`text-sm mt-1 ${
                        showConsoleLogs 
                          ? 'text-gray-700 dark:text-gray-300'
                          : 'text-gray-700 dark:text-gray-300'
                      }`}>
                        {showConsoleLogs 
                          ? 'Los mensajes informativos del sistema aparecen en la consola para depuración:'
                          : 'Los mensajes informativos están silenciados. Solo aparecen:'
                        }
                      </p>
                      <ul className={`text-xs mt-2 space-y-1 list-disc list-inside ${
                        showConsoleLogs 
                          ? 'text-gray-600 dark:text-gray-400'
                          : 'text-gray-600 dark:text-gray-400'
                      }`}>
                        {showConsoleLogs ? (
                          <>
                            <li>Mensajes de estado de caché (datos cargados, actualizaciones)</li>
                            <li>Confirmaciones de IndexedDB (inicialización, sincronización)</li>
                            <li>Notificaciones de sistema (autenticación, configuración)</li>
                            <li>Información de depuración para desarrollo</li>
                          </>
                        ) : (
                          <>
                            <li>Errores críticos del sistema</li>
                            <li>Fallos de autenticación</li>
                            <li>Errores de conexión a Firebase</li>
                            <li>Excepciones no controladas</li>
                          </>
                        )}
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Information Box */}
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <AlertTriangle className="h-5 w-5 text-blue-600 dark:text-blue-400 mt-0.5" />
                    <div className="text-sm text-blue-800 dark:text-blue-200">
                      <h4 className="font-medium mb-2">Información importante:</h4>
                      <ul className="space-y-1 list-disc list-inside">
                        <li>Esta configuración solo afecta mensajes informativos, no errores críticos.</li>
                        <li>Los cambios se aplican inmediatamente en toda la aplicación.</li>
                        <li>Útil para reducir ruido en consola en producción.</li>
                        <li>Los desarrolladores pueden activarlo temporalmente para depuración.</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PermissionGuard>
  );
};

export default Settings;